import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardList, Trash2, PlusCircle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

export const ActivityLogCard = ({ authHeader, refreshKey, onLogAnything }) => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async (m) => {
    try {
      const { data: d } = await axios.get(`${API}/bdm/activity${m ? `?month=${m}` : ""}`, { headers: authHeader() });
      setData(d);
      if (!m) setMonth(d.current_month);
    } catch {
      setData({ items: [], counts: {}, months: [], categories: {}, channels: {} });
    }
  }, [authHeader]);

  useEffect(() => {
    load(month || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const changeMonth = (m) => {
    setMonth(m);
    load(m);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this from your log?")) return;
    try {
      await axios.delete(`${API}/bdm/activity/${id}`, { headers: authHeader() });
      toast.success("Removed");
      load(month);
    } catch {
      toast.error("Could not remove that.");
    }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};
  const categories = data?.categories || {};
  const channels = data?.channels || {};
  const months = data?.months?.length ? data.months : [data?.current_month].filter(Boolean);

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="bdm-activity-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
            <ClipboardList size={13} /> My activity log
          </div>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">This is your month-end report</h3>
          <p className="mt-1 text-sm text-ink-500">
            Log it the day it happens. At month end you copy from here into the NEXA form instead of
            trying to remember four weeks back.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <select
          value={month}
          onChange={(e) => changeMonth(e.target.value)}
          data-testid="bdm-activity-month"
          className="rounded-full border border-linen-border bg-white px-3 py-1.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-teal/40"
        >
          {months.map((m) => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={onLogAnything}
          data-testid="bdm-activity-add"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-lime text-navy hover:brightness-95"
        >
          <PlusCircle className="w-3 h-3" /> Log something
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="bdm-activity-counts">
        {Object.entries(categories).map(([key, label]) => (
          <div key={key} className="rounded-xl bg-linen/70 border border-linen-border px-3 py-2">
            <dt className="text-[10px] uppercase tracking-widest text-ink-500 leading-tight">{label}</dt>
            <dd className="text-lg font-heading text-navy tabular-nums">{counts[key] || 0}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-4 divide-y divide-linen-border">
        {items.length === 0 ? (
          <p className="text-sm text-ink-500 italic py-3" data-testid="bdm-activity-empty">
            Nothing logged for {monthLabel(month)} yet. Tap “Log this” on any asset you use.
          </p>
        ) : (
          items.map((it) => (
            <div key={it.id} className="py-3 flex items-start justify-between gap-3" data-testid={`bdm-activity-row-${it.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy truncate">{it.title}</p>
                <p className="text-[11px] text-ink-500 mt-0.5">
                  {it.occurred_on} · {categories[it.category] || it.category}
                  {channels[it.channel] ? ` · ${channels[it.channel]}` : ""}
                </p>
                {it.note ? <p className="text-xs text-ink-500 mt-1 line-clamp-2">{it.note}</p> : null}
                {it.url ? (
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-teal hover:underline mt-1"
                  >
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => remove(it.id)}
                data-testid={`bdm-activity-delete-${it.id}`}
                className="text-ink-400 hover:text-red-600 shrink-0"
                aria-label="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityLogCard;
